import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Percent, Eye, Heart, MessageCircle, BookOpen, Clock, Search, RefreshCw, Loader2, Ban, ExternalLink, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PromoRulesCard } from "@/components/admin/PromoRulesCard"; // lot 700 — interrupteur + règles

// Lot 698 — Admin › Contenu & Modération › « Promos Marketplace » : toutes les promos en cours (vendeur, remise, fin,
// stats depuis le début de la promo, stories qui la partagent) et les promos terminées (bilan reçu par le vendeur).
// « Arrêter » (droit « gérer ») = fin immédiate avec motif → le vendeur est prévenu, action dans le journal d'audit.
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "—");
const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
const ENDED_BY = { expired: ["Échéance", "#E7F6EC", "#1B7A3D"], seller: ["Arrêtée par le vendeur", "#EEF0F3", "#475467"], admin: ["Arrêtée par la modération", "#FDECEC", "#B42318"] };

const StatChip = ({ icon: Icon, value, title }) => (
  <span className="inline-flex items-center gap-1 text-xs tabular-nums" title={title}><Icon size={12} className="opacity-70" /> {value ?? 0}</span>
);

const Stats = ({ s, id }) => (
  <div className="flex items-center gap-3 flex-wrap" data-testid={`promo-stats-${id}`}>
    <StatChip icon={Eye} value={s?.views} title="Vues (1 par membre, par provenance et par jour)" />
    <StatChip icon={Heart} value={s?.favorites} title="Favoris ajoutés" />
    <StatChip icon={MessageCircle} value={s?.messages} title="Messages reçus par le vendeur" />
    <StatChip icon={BookOpen} value={s?.story_opens} title="Membres venus de la story du vendeur" />
  </div>
);

const Thumb = ({ src }) => (
  src ? <img src={resolveMediaUrl(src)} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" loading="lazy" />
    : <div className="h-12 w-12 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--yalola-surface-alt)" }}><ImageIcon size={16} className="opacity-40" /></div>
);

export const MarketplacePromosPanel = ({ canManage = true, canEditRules = false }) => {
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/marketplace/promos?status=${status}&q=${encodeURIComponent(q.trim())}`).then(({ data }) => setData(data)).catch(() => setData({ items: [], summary: {} })).finally(() => setLoading(false));
  }, [status, q]);
  useEffect(() => { const h = setTimeout(load, q ? 300 : 0); return () => clearTimeout(h); }, [load, q]);
  const stop = async (it) => {
    const reason = window.prompt(`Arrêter la promo -${it.promo?.percent} % sur « ${it.title} » ?\n\nMotif envoyé au vendeur (facultatif) :`, "");
    if (reason === null) return;
    setBusy(it.id);
    try {
      await api.post(`/admin/marketplace/promos/${it.id}/stop`, { reason });
      toast.success(`Promo arrêtée — ${it.seller?.name || "le vendeur"} a été prévenu`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Arrêt impossible"); }
    finally { setBusy(""); }
  };
  const items = data?.items || [];
  const sum = data?.summary || {};
  return (
    <div className="space-y-4" data-testid="admin-promos-panel">
      <PromoRulesCard canEdit={canEditRules} />
      <div className="yalola-card p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <Percent size={18} style={{ color: "#D64545" }} />
          <div className="font-bold font-display text-lg">Promos Marketplace</div>
          <span className="chip text-[11px]" style={{ background: "#FDECEC", color: "#B42318" }} data-testid="admin-promos-active-count">{sum.active ?? 0} en cours</span>
          <span className="chip text-[11px]" style={{ background: "var(--yalola-surface-alt)" }} data-testid="admin-promos-ended-count">{sum.ended_30d ?? 0} terminées · 30 j</span>
          <button onClick={load} disabled={loading} className="ml-auto text-[11px] flex items-center gap-1 rounded-full px-2.5 py-1 hover:bg-black/5 disabled:opacity-50" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-promos-refresh">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Actualiser
          </button>
        </div>
        <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
          Remises temporaires posées par les vendeurs (prix barré, badge « -X % »). Les compteurs courent depuis le début de la promo ; « story » = membres arrivés via la story du vendeur. Arrêter une promo rétablit le prix de référence et prévient le vendeur avec ton motif.
        </p>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <div className="flex rounded-full p-0.5" style={{ background: "var(--yalola-surface-alt)" }}>
            {[["active", "En cours"], ["ended", "Terminées"]].map(([k, l]) => (
              <button key={k} onClick={() => setStatus(k)} className="px-3 py-1 rounded-full text-xs font-semibold" style={status === k ? { background: "var(--yalola-primary)", color: "#fff" } : {}} data-testid={`admin-promos-tab-${k}`}>{l}</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titre, ville ou vendeur…" className="pl-8 h-9 rounded-full" data-testid="admin-promos-search" />
          </div>
        </div>
      </div>

      <div className="yalola-card p-2">
        {loading && !data && <div className="p-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}><Loader2 size={16} className="animate-spin inline" /></div>}
        {data && items.length === 0 && (
          <div className="p-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-promos-empty">
            {status === "active" ? "Aucune promo en cours." : "Aucune promo terminée ces 30 derniers jours."}
          </div>
        )}
        <div className="divide-y" style={{ borderColor: "var(--yalola-border)" }}>
          {items.map((it) => {
            const p = it.promo || {};
            const ended = status === "ended";
            const [eLabel, eBg, eFg] = ENDED_BY[it.ended_by] || ENDED_BY.expired;
            return (
              <div key={`${it.id}-${it.ended_at || ""}`} className="p-3 flex items-start gap-3 flex-wrap" data-testid={`promo-row-${it.id}`} data-percent={p.percent}>
                <Thumb src={it.image} />
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate max-w-[280px]" data-testid={`promo-title-${it.id}`}>{it.title}</span>
                    <span className="rounded-full px-2 py-0.5 text-[11px] font-extrabold text-white" style={{ background: "#D64545" }} data-testid={`promo-percent-${it.id}`}>-{p.percent} %</span>
                    {it.active === false && <span className="chip text-[10px]" style={{ background: "#EEF0F3", color: "#475467" }}>Vendue</span>}
                    {ended && <span className="chip text-[10px]" style={{ background: eBg, color: eFg }} data-testid={`promo-ended-by-${it.id}`}>{eLabel}</span>}
                  </div>
                  <div className="text-xs mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
                    <span className="tabular-nums"><b style={{ color: "#D64545" }}>{p.price} {it.currency}</b> <s>{it.price} {it.currency}</s></span>
                    <span>·</span>
                    {it.seller?.id ? <Link to={`/profile/${it.seller.id}`} className="hover:underline" data-testid={`promo-seller-${it.id}`}>{it.seller.name}</Link> : <span>—</span>}
                    {it.city && <><span>·</span><span>{it.city}</span></>}
                    <span>·</span>
                    {ended
                      ? <span className="inline-flex items-center gap-1"><Clock size={11} /> du {fmtDay(p.started_at)} au {fmtDay(it.ended_at)}</span>
                      : <span className="inline-flex items-center gap-1" data-testid={`promo-ends-${it.id}`}><Clock size={11} /> fin le {fmtDay(p.ends_at)} ({daysLeft(p.ends_at)} j)</span>}
                    {!ended && it.stories > 0 && <span className="chip text-[10px]" style={{ background: "#FDECEC", color: "#B42318" }} data-testid={`promo-stories-${it.id}`}>{it.stories} story</span>}
                  </div>
                  <div className="mt-1.5"><Stats s={it.stats} id={it.id} /></div>
                  {ended && it.reason && <div className="text-xs italic mt-1" style={{ color: "#B42318" }} data-testid={`promo-reason-${it.id}`}>Motif : {it.reason}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/marketplace?item=${it.id}`} className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border hover:bg-black/5" style={{ borderColor: "var(--yalola-border)" }} data-testid={`promo-open-${it.id}`}>
                    <ExternalLink size={12} /> Ouvrir
                  </Link>
                  {!ended && canManage && (
                    <Button size="sm" variant="outline" className="rounded-full h-8 text-red-600" disabled={busy === it.id} onClick={() => stop(it)} data-testid={`promo-stop-${it.id}`}>
                      {busy === it.id ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} <span className="ml-1">Arrêter</span>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MarketplacePromosPanel;
